import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { API_BASE_URL } from "../utils/config";

// Routes each non-admin role is allowed to open. Everything else redirects
// them back to their `fallback` (their default landing page). Any role not
// listed here (including the basic "user" role) falls back to the "user"
// entry, matching the original single-list behavior.
//
// The Sekretaris/Bendahara entries deliberately do NOT hardcode their own
// "/administrasi/<module>" prefix here — that's computed from
// `accessible_modules` below instead, the same additive list Sidebar.jsx
// already renders its nav tree from (backed by inventory.teams.user_modules).
// A hardcoded prefix would only ever allow a role into its own module, so
// someone whose kelompok additionally grants the other Administrasi module
// (e.g. a Bendahara-role account also holding a Sekretariat membership) would
// see the extra nav item in the sidebar but get bounced back to /dashboard
// the moment they clicked it.
const ROLE_GUARD = {
  user: {
    allowed: ["/transactions", "/profile", "/panel-assemblies", "/my-loan-requests"],
    fallback: "/transactions",
  },
  sekretaris: {
    allowed: ["/", "/dashboard", "/profile", "/administrasi/template-manager", "/administrasi/folder-config"],
    fallback: "/dashboard",
  },
  bendahara: {
    allowed: ["/", "/dashboard", "/profile", "/administrasi/template-manager", "/administrasi/folder-config"],
    fallback: "/dashboard",
  },
};

const ADMINISTRASI_MODULES = ["sekretaris", "bendahara"];

function DashboardLayout({ children }) {
  // Mobile/tablet slide-in drawer (the desktop sidebar is hidden below md)
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [modules, setModules] = useState([]);
  const [roleLoaded, setRoleLoaded] = useState(false);

  // Resolve the current user's role (+ additive accessible_modules) once,
  // used to gate navigation.
  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setRoleLoaded(true);
      return;
    }
    fetch(`${API_BASE_URL}/api/auth/me/`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        setRole(data?.role ?? null);
        setModules(data?.accessible_modules || []);
      })
      .catch(() => setRole(null))
      .finally(() => setRoleLoaded(true));
  }, []);

  // Confine non-privileged roles (anyone who isn't admin/superadmin) to their
  // allowed routes. We wait until the role has actually loaded so admins aren't
  // bounced during the in-flight request.
  useEffect(() => {
    if (!roleLoaded) return;
    if (role === "admin" || role === "superadmin") return;
    const guard = ROLE_GUARD[role] || ROLE_GUARD.user;
    const modulePaths = modules
      .filter((m) => ADMINISTRASI_MODULES.includes(m))
      .map((m) => `/administrasi/${m}`);
    const allowedPrefixes = [...guard.allowed, ...modulePaths];
    const allowed = allowedPrefixes.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
    );
    if (!allowed) navigate(guard.fallback, { replace: true });
  }, [role, modules, roleLoaded, location.pathname, navigate]);

  // Auto-close the mobile drawer whenever the route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Prevent the page behind the drawer from scrolling while it's open on mobile
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  return (
    <div className="app-ambient h-screen overflow-hidden">
      <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />

      {/* The sidebar is a fixed 264px rail from md up; the drawer overlays below it. */}
      <div className="md:ml-[264px] h-screen flex flex-col min-w-0">
        <Topbar onMenuClick={() => setMobileOpen(true)} />

        {/* Only this scrolls */}
        <main className="flex-1 overflow-y-auto px-[clamp(20px,3vw,40px)] pt-8 pb-14">
          <div className="w-full max-w-[1360px] animate-fade-slide-up">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
